import React from "react";
import { useTranslation } from "react-i18next";
import { DEFAULT_WALL_HEIGHT } from "../../../../features/floorplan3d/geometry";
import { VoidType } from "../../../../types/floorplannerTypes";
import DimensionField from "../components/DimensionField";
import RotationField from "../components/RotationField";
import MetricsSection from "../components/MetricsSection";
import View3DSection from "../components/View3DSection";
import UnitHeader from "../components/UnitHeader";
import type { PropertyPanelController } from "../types";
import {
  degreesToRadians,
  getCommonDegreesValue,
  getCommonMillimeterValue,
  panelGridFieldClassName,
  panelObjectTitleRowClassName,
  radiansToRoundedDegrees,
  toDisplayLength } from "../propertyPanelUtils";

type VoidSectionProps = Pick<PropertyPanelController, "updateGlobalState"> & {
  selectedVoids: VoidType[];
  displayedVoids: VoidType[];
  is3DView: boolean;
};

const getVoidHeight = (voidSymbol: VoidType) =>
  voidSymbol.threeD?.height ?? DEFAULT_WALL_HEIGHT;
const getVoidBaseElevation = (voidSymbol: VoidType) =>
  voidSymbol.threeD?.baseElevation ?? 0;
const getVoidTopBulge = (voidSymbol: VoidType) =>
  voidSymbol.threeD?.topBulge ?? 0;

const VoidSection: React.FC<VoidSectionProps> = ({
  selectedVoids,
  displayedVoids,
  updateGlobalState,
  is3DView }) => {
  const { t } = useTranslation();
  const mixed = t("common.mixed");

  return (
    <>
      {displayedVoids.map((voidSymbol) => {
        if (!voidSymbol) return null;
        return (
          <div key={`void-${voidSymbol?.id}`}>
            <div className="border-b border-gray-300" />
            <div className="text-sm font-normal">
              <div className={panelObjectTitleRowClassName}>
                <UnitHeader
                  title={
                    selectedVoids.length > 1
                      ? t("propertyPanel.multipleVoids", { count: selectedVoids.length })
                      : t("symbols.void")
                  }
                />
              </div>
              <MetricsSection>
                <div
                  className={`grid items-end gap-2 py-1 ${
                    is3DView ? "grid-cols-2" : "grid-cols-1"
                  }`}
                >
                  <DimensionField
                    label={t("propertyPanel.length")}
                    fieldClassName={panelGridFieldClassName}
                    value={
                      selectedVoids.length > 1
                        ? (getCommonMillimeterValue(
                            selectedVoids,
                            (symbol) => symbol.voidLength,
                          ) ?? "")
                        : toDisplayLength(voidSymbol?.voidLength || 0)
                    }
                    placeholder={
                      selectedVoids.length > 1 ? mixed : undefined
                    }
                    onChange={(nextValue) =>
                      updateGlobalState(
                        selectedVoids,
                        "voidLength",
                        parseFloat(nextValue),
                      )
                    }
                  />
                  {is3DView && (
                    <DimensionField
                      label={t("propertyPanel.height")}
                      fieldClassName={panelGridFieldClassName}
                      value={
                        selectedVoids.length > 1
                          ? (getCommonMillimeterValue(
                              selectedVoids,
                              getVoidHeight,
                            ) ?? "")
                          : toDisplayLength(getVoidHeight(voidSymbol))
                      }
                      placeholder={
                        selectedVoids.length > 1 ? mixed : undefined
                      }
                      onChange={(nextValue) =>
                        updateGlobalState(
                          selectedVoids,
                          "symbol3DHeight",
                          parseFloat(nextValue),
                        )
                      }
                    />
                  )}
                </div>
              </MetricsSection>
              {is3DView && (
                <View3DSection>
                  <div className="grid grid-cols-2 items-end gap-2 py-1">
                    <DimensionField
                      label={t("propertyPanel.distanceFromFloor")}
                      fieldClassName={panelGridFieldClassName}
                      min={0}
                      value={
                        selectedVoids.length > 1
                          ? (getCommonMillimeterValue(
                              selectedVoids,
                              getVoidBaseElevation,
                            ) ?? "")
                          : toDisplayLength(
                              getVoidBaseElevation(voidSymbol),
                            )
                      }
                      placeholder={
                        selectedVoids.length > 1 ? mixed : undefined
                      }
                      onChange={(nextValue) =>
                        updateGlobalState(
                          selectedVoids,
                          "symbol3DBaseElevation",
                          parseFloat(nextValue),
                        )
                      }
                    />
                    <DimensionField
                      label={t("propertyPanel.topBulge")}
                      fieldClassName={panelGridFieldClassName}
                      value={
                        selectedVoids.length > 1
                          ? (getCommonMillimeterValue(
                              selectedVoids,
                              getVoidTopBulge,
                            ) ?? "")
                          : toDisplayLength(getVoidTopBulge(voidSymbol))
                      }
                      placeholder={
                        selectedVoids.length > 1 ? mixed : undefined
                      }
                      onChange={(nextValue) =>
                        updateGlobalState(
                          selectedVoids,
                          "symbol3DTopBulge",
                          parseFloat(nextValue),
                        )
                      }
                    />
                  </div>
                </View3DSection>
              )}
              <div className="grid grid-cols-2 items-end gap-2 px-3 py-2">
                <RotationField
                  value={
                    selectedVoids.length > 1
                      ? (getCommonDegreesValue(selectedVoids) ?? "")
                      : radiansToRoundedDegrees(voidSymbol?.rotation || 0)
                  }
                  placeholder={
                    selectedVoids.length > 1 ? mixed : undefined
                  }
                  onChange={(degrees) =>
                    updateGlobalState(
                      selectedVoids,
                      "rotation",
                      degreesToRadians(degrees),
                    )
                  }
                />
              </div>
            </div>
            <div className="border-b border-gray-300" />
          </div>
        );
      })}
    </>
  );
};

export default VoidSection;
